import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PercentIcon from "@mui/icons-material/Percent";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";

// MUI Components
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import MuiCard from "../../components/mui/MuiCard";

// API
import { postSalesperson } from "../../helpers/api_helper";

/**
 * MuiSalespersonCreate - Modern Salesperson creation form
 *
 * UX Principles Applied:
 * - Jakob's Law: Familiar form patterns
 * - Miller's Law: Limited fields (4 total)
 * - Law of Proximity: Related fields grouped
 * - Von Restorff Effect: Primary action highlighted
 */
const MuiSalespersonCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  document.title = `${t("Create Salesperson")} | FlexFlow ERP`;

  // Form state
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [firstDiscount, setFirstDiscount] = useState("");
  const [normalDiscount, setNormalDiscount] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Validation
  const isFormValid = name.trim() && surname.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      setSnackbar({
        open: true,
        message: t("Please fill in all required fields"),
        severity: "warning",
      });
      return;
    }

    setLoading(true);

    const discounts = {
      firstDiscount: firstDiscount || 0,
      normalDiscount: normalDiscount || 0,
    };

    const payload = {
      resource: [
        {
          name: name.trim(),
          surname: surname.trim(),
          discounts: JSON.stringify(discounts),
        },
      ],
    };

    try {
      const res = await postSalesperson(payload);

      if (res && res.error) {
        // Handle specific errors
        let errorMessage = res.error.message || t("Failed to create salesperson");

        if (res.error.message?.includes("does not exist")) {
          errorMessage = t("Database table not configured. Please contact administrator.");
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      } else if (res && res.resource) {
        setSnackbar({
          open: true,
          message: t("Salesperson created successfully"),
          severity: "success",
        });
        // Navigate to salespersons list after short delay
        setTimeout(() => navigate("/salespersons"), 1000);
      }
    } catch (err) {
      console.error("Error creating salesperson:", err);
      setSnackbar({
        open: true,
        message: t("An unexpected error occurred"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const breadcrumbs = [
    { label: t("Salespersons"), path: "/salespersons" },
    { label: t("Create Salesperson") },
  ];

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="md">
        <MuiPageHeader
          title={t("Create Salesperson")}
          subtitle={t("Add a new salesperson to the team")}
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/salespersons")}
            >
              {t("Back")}
            </Button>
          }
        />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Main Form Card */}
            <Grid item xs={12} md={8}>
              <MuiCard title={t("Personal Information")} showDivider>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label={t("First Name")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("Enter first name")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label={t("Last Name")}
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      placeholder={t("Enter last name")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </MuiCard>

              <MuiCard title={t("Discount Settings")} showDivider sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t("Configure default discount percentages for this salesperson")}
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("First Order Discount")}
                      value={firstDiscount}
                      onChange={(e) => setFirstDiscount(e.target.value)}
                      placeholder="0"
                      helperText={t("Discount for first-time customers")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalOfferIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("Standard Discount")}
                      value={normalDiscount}
                      onChange={(e) => setNormalDiscount(e.target.value)}
                      placeholder="0"
                      helperText={t("Default discount for regular orders")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PercentIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                </Grid>
              </MuiCard>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <MuiCard title={t("Summary")} showDivider>
                <Box sx={{ textAlign: "center", py: 2 }}>
                  {/* Avatar Preview */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: name || surname ? "primary.main" : "grey.300",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                      fontWeight: 600,
                      mx: "auto",
                      mb: 2,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {name?.charAt(0)?.toUpperCase() || ""}
                    {surname?.charAt(0)?.toUpperCase() || ""}
                    {!name && !surname && <PersonIcon sx={{ fontSize: 36 }} />}
                  </Box>

                  <Typography variant="h6" fontWeight={600}>
                    {name || surname
                      ? `${name} ${surname}`.trim()
                      : t("New Salesperson")}
                  </Typography>

                  {(firstDiscount || normalDiscount) && (
                    <Box sx={{ mt: 2, textAlign: "left" }}>
                      <Divider sx={{ mb: 2 }} />
                      {firstDiscount && (
                        <Typography variant="body2" color="text.secondary">
                          {t("First Discount")}: {firstDiscount}%
                        </Typography>
                      )}
                      {normalDiscount && (
                        <Typography variant="body2" color="text.secondary">
                          {t("Standard Discount")}: {normalDiscount}%
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </MuiCard>

              {/* Action Buttons */}
              <MuiCard sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={!isFormValid || loading}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <SaveIcon />
                  }
                  sx={{ mb: 2 }}
                >
                  {loading ? t("Creating...") : t("Create Salesperson")}
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate("/salespersons")}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
              </MuiCard>
            </Grid>
          </Grid>
        </form>

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MuiSalespersonCreate;
